import { Link } from "react-router-dom";
import { Lock, ArrowRight, Sparkles } from "lucide-react";

// Renders in the dashboard main area when a route is locked for the
// current user's tier or trial state. `reason` picks the copy + upgrade
// destination; `moduleName` is displayed inline.
//   reason="trial"       — 24-hour trial user hit a paid-only route
//   reason="kickstart_1" — K1 user hit K2+ route → upsell K2
//   reason="kickstart_2" — K2 user hit K3+ route → upsell K3
//   reason="kickstart_3" — K3 user hit a non-Kickstart module → upsell to Starter+
//   reason="kickstart_locked" — generic fallback → pricing
export default function LockedModule({ moduleName, reason = "trial" }) {
  let eyebrow = "Locked during your free trial";
  let subtitle =
    "Your 24-hour free trial gives you full access to the four AI assistants — Zyntha, Thoro, Zyona, and Zynthoro Assist. Upgrade to a paid plan to unlock every module.";
  let ctaLabel = "See plans";
  let ctaTo = "/pricing";

  if (reason === "kickstart_1") {
    eyebrow = "Included in Kickstart 2";
    subtitle =
      "Your Kickstart 1 licence unlocks Planning, Time Tracking, Communication and the four AI assistants. Upgrade to Kickstart 2 to unlock Finance, Sales and AI Studio.";
    ctaLabel = "Upgrade to Kickstart 2";
    ctaTo = "/subscribe/kickstart_2";
  } else if (reason === "kickstart_2") {
    eyebrow = "Included in Kickstart 3";
    subtitle =
      "Your Kickstart 2 licence adds Finance, Sales and AI Studio. Upgrade to Kickstart 3 to unlock Accounting, Operations, Projects and Marketing.";
    ctaLabel = "Upgrade to Kickstart 3";
    ctaTo = "/subscribe/kickstart_3";
  } else if (reason === "kickstart_3" || reason === "kickstart_locked") {
    eyebrow = "Upgrade available";
    subtitle =
      "This module is included on Starter and above. Compare plans to pick the right subscription for your business.";
    ctaLabel = "See plans";
    ctaTo = "/pricing";
  }

  return (
    <div
      data-testid="locked-module"
      data-reason={reason}
      className="max-w-2xl mx-auto text-center py-14 sm:py-20"
    >
      <div
        className="inline-flex items-center justify-center rounded-2xl mb-6"
        style={{ width: 72, height: 72, background: "#202B6014" }}
      >
        <Lock size={28} style={{ color: "var(--zy-blue)" }} />
      </div>

      <p
        className="uppercase tracking-[0.18em] text-[12px] mb-3 notranslate"
        style={{ color: "var(--zy-blue)" }}
        translate="no"
      >
        {eyebrow}
      </p>

      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1628] leading-[1.15]">
        {moduleName ? `${moduleName} is not on your plan.` : "This module is not on your plan."}
      </h2>

      <p className="mt-5 text-[15px] text-black/60 leading-relaxed max-w-lg mx-auto">
        {subtitle}
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to={ctaTo}
          data-testid="locked-module-upgrade"
          className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 notranslate"
          style={{ background: "var(--zy-blue)" }}
          translate="no"
        >
          {ctaLabel} <ArrowRight size={15} />
        </Link>
        <Link
          to="/dashboard/zyntha"
          data-testid="locked-module-ai"
          className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[14px] font-semibold text-[var(--zy-blue)] border border-[var(--zy-blue)]/25 hover:bg-[var(--zy-blue)]/[0.05] transition-colors"
        >
          <Sparkles size={14} /> Chat with an AI assistant
        </Link>
      </div>
    </div>
  );
}
